import { axiosInstance } from "../../../api/axiosInstance";
import { LeaveApplyRequest, LeaveRequest } from "../types";

/**
 * Leave requests. Faculty (My Space → My Leave):
 *   GET  /leave/me                  → LeaveRequest[]   own requests, latest first
 *   POST /leave/me                  → LeaveRequest     apply (PENDING)
 *   POST /leave/me/{id}/cancel      → LeaveRequest     PENDING, or APPROVED before it starts
 * Admin (Staff → Leave Requests):
 *   GET  /leave                     → LeaveRequest[]   every faculty request in the active year
 *   POST /leave                     → LeaveRequest     apply on a faculty's behalf (APPROVED at once)
 *   POST /leave/{id}/approve|reject  { note? }  → LeaveRequest
 * Errors: 400 / 409 with a plain-text reason.
 */

// The backend drops nulls, false and empty values from JSON (jackson non_empty) — put them back.
const normalize = (leave: Partial<LeaveRequest>): LeaveRequest => ({
  id: leave.id!,
  applicantType: leave.applicantType ?? "FACULTY",
  facultyId: leave.facultyId ?? null,
  applicantName: leave.applicantName ?? null,
  fromDate: leave.fromDate!,
  toDate: leave.toDate!,
  halfDay: leave.halfDay ?? null,
  leaveType: leave.leaveType!,
  reason: leave.reason ?? "",
  status: leave.status!,
  days: leave.days ?? 0,
  periodsAffected: leave.periodsAffected ?? 0,
  appliedAt: leave.appliedAt ?? null,
  decidedByName: leave.decidedByName ?? null,
  decidedAt: leave.decidedAt ?? null,
  decisionNote: leave.decisionNote ?? null,
  cancellable: Boolean(leave.cancellable),
});

export const fetchMyLeave = async (): Promise<LeaveRequest[]> => {
  const response = await axiosInstance.get<Partial<LeaveRequest>[]>("/leave/me");
  return (response.data ?? []).map(normalize);
};

export const applyMyLeave = async (request: LeaveApplyRequest): Promise<LeaveRequest> => {
  const response = await axiosInstance.post<Partial<LeaveRequest>>("/leave/me", request);
  return normalize(response.data);
};

export const cancelMyLeave = async (id: number): Promise<LeaveRequest> => {
  const response = await axiosInstance.post<Partial<LeaveRequest>>(`/leave/me/${id}/cancel`);
  return normalize(response.data);
};

export const fetchFacultyLeave = async (): Promise<LeaveRequest[]> => {
  const response = await axiosInstance.get<Partial<LeaveRequest>[]>("/leave");
  return (response.data ?? []).map(normalize);
};

export const applyLeaveForFaculty = async (request: LeaveApplyRequest): Promise<LeaveRequest> => {
  const response = await axiosInstance.post<Partial<LeaveRequest>>("/leave", request);
  return normalize(response.data);
};

export const decideLeave = async (id: number, decision: "approve" | "reject", note: string): Promise<LeaveRequest> => {
  const response = await axiosInstance.post<Partial<LeaveRequest>>(`/leave/${id}/${decision}`, { note });
  return normalize(response.data);
};

/** The backend's plain-text reason, or the fallback. */
export const leaveErrorMessage = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  return typeof data === "string" && data.trim() ? data : fallback;
};
